import dayjs from 'dayjs';
import { Moves } from '@gql/base-types';

/**
 * Get the date a move should be displayed on.
 * Uses cascading fallback through status timestamps to find the actual activity date.
 * Falls back to ready_by if no activity has occurred.
 */
export const getMoveDisplayDate = (move: Moves, timezone: string): string => {
  const {
    pickup_started,
    pickup_arrived,
    pickup_successful,
    delivery_started,
    delivery_arrived,
    delivery_successful,
    ready_by,
  } = move;

  // Use actual activity timestamps (driver actions) to determine display date
  const activityDate =
    pickup_started ||
    pickup_arrived ||
    pickup_successful ||
    delivery_started ||
    delivery_arrived ||
    delivery_successful;

  if (activityDate) {
    return dayjs(activityDate).tz(timezone).format('YYYY-MM-DD');
  }

  // For unstarted moves, use ready_by (appointment date)
  return ready_by ? dayjs(ready_by).tz(timezone).format('YYYY-MM-DD') : '';
};

/**
 * Get the timestamp to use for positioning a move on the timeline.
 * For in-progress/completed moves on their activity date, uses the activity timestamp.
 * Otherwise uses ready_by (appointment time).
 */
export const getMoveDisplayTime = (move: Moves, selectedDate: string, timezone: string): string | null => {
  const {
    pickup_started,
    pickup_arrived,
    pickup_successful,
    delivery_started,
    delivery_arrived,
    delivery_successful,
    ready_by,
  } = move;

  // Use actual activity timestamps for positioning (driver actions only)
  const activityTimestamp =
    pickup_started ||
    pickup_arrived ||
    pickup_successful ||
    delivery_started ||
    delivery_arrived ||
    delivery_successful;

  // If there's activity and it's on the selected date, use that timestamp
  if (activityTimestamp) {
    const activityDate = dayjs(activityTimestamp).tz(timezone).format('YYYY-MM-DD');
    if (activityDate === selectedDate) {
      return activityTimestamp;
    }
  }

  // Default to ready_by for scheduled (not yet started) moves
  return ready_by || null;
};

/**
 * Get the actual duration of a move in minutes.
 * For worked moves (pickup_started to delivery_successful), returns actual duration.
 * For pending moves, returns null (caller should use lane.duration_sec).
 */
export const getActualMoveDurationMinutes = (move: Moves): number | null => {
  const { pickup_started, delivery_successful } = move;

  // Both timestamps required for actual duration
  if (!pickup_started || !delivery_successful) {
    return null;
  }

  const startTime = dayjs(pickup_started);
  const endTime = dayjs(delivery_successful);
  const durationMinutes = endTime.diff(startTime, 'minute');

  // Sanity check: return null if duration is negative or unreasonably large
  if (durationMinutes < 0 || durationMinutes > 1440) {
    // max 24 hours
    return null;
  }

  return durationMinutes;
};
